import { defineMessages } from 'react-intl';

type MessageKeys =
	| 'createSpaceModalTitle'
	| 'createSpaceIntro'
	| 'createSpaceNameLabel'
	| 'createSpaceNameHelperMessage'
	| 'createSpaceIconLabel'
	| 'createSpaceIconHelperMessage'
	| 'createSpaceAboutLabel'
	| 'createSpaceSourcesLabel'
	| 'createSpaceSourcesHelperMessage'
	| 'createSpaceAddSources'
	| 'createSpaceAddSourcesModalTitle'
	| 'createSpaceAddSourcesModalPlaceholder'
	| 'createSpaceRemoveSource'
	| 'createSpaceNameRequired'
	| 'createSpaceEmojiPickerLabel'
	| 'createSpaceCancel'
	| 'createSpaceSubmit'
	| 'createSpaceRequiredFieldsHint'
	| 'createSpaceFirstSpaceOnboardingSpotlightTitle'
	| 'createSpaceFirstSpaceOnboardingSpotlightDescription'
	| 'createSpaceFirstSpaceOnboardingSpotlightCta';

const message: Record<MessageKeys, { id: string; defaultMessage: string; description?: string }> =
	defineMessages({
		createSpaceModalTitle: {
			id: 'ai-mate.rovo-spaces.create-space-modal.title',
			defaultMessage: 'Create Rovo space',
			description: 'Title of the modal for creating a Rovo space from Rovo chat navigation.',
		},
		createSpaceIntro: {
			id: 'ai-mate.rovo-spaces.create-space-modal.intro',
			defaultMessage:
				'Set up a space for a project, topic, or team. Give your space a name, add relevant knowledge sources to receive chat answers based on that context.',
			description: 'Introductory guidance shown near the top of the create Rovo space modal.',
		},
		createSpaceNameLabel: {
			id: 'ai-mate.rovo-spaces.create-space-modal.name-label',
			defaultMessage: 'Name',
			description: 'Label for the required Rovo space name field.',
		},
		createSpaceNameHelperMessage: {
			id: 'ai-mate.rovo-spaces.create-space-modal.name-helper',
			defaultMessage:
				'Give your Rovo space a clear name so you can identify the project or topic at a glance.',
			description: 'Helper text under the Rovo space name field in the create Rovo space modal.',
		},
		createSpaceIconLabel: {
			id: 'ai-mate.rovo-spaces.create-space-modal.icon-label',
			defaultMessage: 'Icon',
			description: 'Label for the icon picker field in the create Rovo space modal.',
		},
		createSpaceIconHelperMessage: {
			id: 'ai-mate.rovo-spaces.create-space-modal.icon-helper',
			defaultMessage: 'Select an icon to represent your Rovo space',
			description: 'Helper text under the icon picker field in the create Rovo space modal.',
		},
		createSpaceAboutLabel: {
			id: 'ai-mate.rovo-spaces.create-space-modal.about-label',
			defaultMessage: 'Description',
			description: 'Label for the optional Rovo space description field.',
		},
		createSpaceSourcesLabel: {
			id: 'ai-mate.rovo-spaces.create-space-modal.sources-label',
			defaultMessage: 'Knowledge',
			description: 'Label for the Rovo space knowledge sources field.',
		},
		createSpaceSourcesHelperMessage: {
			id: 'ai-mate.rovo-spaces.create-space-modal.sources-helper',
			defaultMessage:
				'Add knowledge such as links to docs, pages, or files that your chat can answer from.',
			description: 'Helper text under the knowledge field in the create space modal.',
		},
		createSpaceAddSources: {
			id: 'ai-mate.rovo-spaces.create-space-modal.add-sources',
			defaultMessage: 'Add knowledge',
			description: 'Button to open the flow for adding more knowledge to the Rovo space.',
		},
		createSpaceAddSourcesModalTitle: {
			id: 'ai-mate.rovo-spaces.create-space-modal.add-sources-modal-title',
			defaultMessage: 'Add sources',
			description:
				'Title of the secondary modal for adding sources (placeholder until designs land).',
		},
		createSpaceAddSourcesModalPlaceholder: {
			id: 'ai-mate.rovo-spaces.create-space-modal.add-sources-placeholder',
			defaultMessage: 'Source picker coming soon.',
			description: 'Placeholder body text in the add-sources modal until the real UI is built.',
		},
		createSpaceRemoveSource: {
			id: 'ai-mate.rovo-spaces.create-space-modal.remove-source',
			defaultMessage: 'Remove {name}',
			description:
				'Accessibility label for the delete control on a source row; {name} is the source title.',
		},
		createSpaceNameRequired: {
			id: 'ai-mate.rovo-spaces.create-space-modal.name-required',
			defaultMessage: 'Enter a name',
			description: 'Validation error when Rovo space name is empty.',
		},
		createSpaceEmojiPickerLabel: {
			id: 'ai-mate.rovo-spaces.create-space-modal.emoji-picker',
			defaultMessage: 'Choose icon',
			description: 'Accessibility label for opening the icon picker in the create space modal.',
		},
		createSpaceCancel: {
			id: 'ai-mate.rovo-spaces.create-space-modal.cancel',
			defaultMessage: 'Cancel',
			description: 'Button to close the create space modal without saving.',
		},
		createSpaceSubmit: {
			id: 'ai-mate.rovo-spaces.create-space-modal.submit',
			defaultMessage: 'Create',
			description: 'Primary button to submit the create space form.',
		},
		createSpaceRequiredFieldsHint: {
			id: 'ai-mate.rovo-spaces.create-space-modal.required-fields-hint',
			defaultMessage: 'Required fields are marked with an asterisk <asterisk></asterisk>',
			description:
				'Informational text in the create space form header: required fields use an asterisk; the asterisk is rendered via the rich-text placeholder.',
		},
		createSpaceFirstSpaceOnboardingSpotlightTitle: {
			id: 'ai-mate.rovo-spaces.create-space-modal.first-space-onboarding-spotlight-title',
			defaultMessage: 'Set up your space',
			description:
				'Title for an onboarding spotlight pointing to the Add knowledge button in the create Rovo space modal.',
		},
		createSpaceFirstSpaceOnboardingSpotlightDescription: {
			id: 'ai-mate.rovo-spaces.create-space-modal.first-space-onboarding-spotlight-description',
			defaultMessage: 'Give your space a name and description, then add knowledge sources.',
			description:
				'Body text for an onboarding spotlight pointing to the Add knowledge button in the create Rovo space modal.',
		},
		createSpaceFirstSpaceOnboardingSpotlightCta: {
			id: 'ai-mate.rovo-spaces.create-space-modal.first-space-onboarding-spotlight-cta',
			defaultMessage: 'Done',
			description:
				'Primary action label for dismissing the onboarding spotlight in the create Rovo space modal.',
		},
	});

export default message;
